import { combineReducers } from 'redux';
import { isEmpty } from 'lodash';
import * as adReviewActionTypes from 'actions/adReview';
import * as debug from 'actions/debug';
import * as filterAdActionTypes from 'actions/filterAds';
import { fuzzyMatch } from 'utilities';
import { PAGINATION_PAGE_LIMIT } from 'config';

import { DEFAULT_PARAMS } from 'constants/BrowseAds';

export const approvalActionTypeRegex = /^(APPROVE|CANCEL|REJECT|REVISE|KICK)_AD_(SUBMITTED|SUCCEEDED|FAILED)$/;

export function approval(state = {}, action) {
  if (!action.type || !action.type.match(approvalActionTypeRegex)) {
    return state;
  }

  return {
    ...state,
    [action.id]: {
      approvalStatus: action.type,
      errorMsg: action.err || '',
    },
  };
}

export function onboarding(state = {
  shouldCheck: true,
  show: false,
}, action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_ADS_SUCCESS:
      // on the first fetch, determine whether the user has any ads.
      // TODO: this should be a flag on the account to avoid any odd cases
      // where the user deep links into a page that has no ads.
      if (state.shouldCheck) {
        return {
          shouldCheck: false,
          show: isEmpty(action.ads),
        };
      }

      return state;

    default:
      return state;
  }
}

export function adNotFound(state = false, action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_AD_REQUESTED:
      return false;
    case adReviewActionTypes.FETCH_AD_NOT_FOUND:
      return true;
    case adReviewActionTypes.FETCH_AD_SUCCESS:
      return false;
    case adReviewActionTypes.FETCH_AD_FAILED:
      return false;
    default:
      return state;
  }
}

export function fetchingAd(state = false, action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_AD_REQUESTED:
      return true;
    case adReviewActionTypes.FETCH_AD_NOT_FOUND:
      return false;
    case adReviewActionTypes.FETCH_AD_SUCCESS:
      return false;
    case adReviewActionTypes.FETCH_AD_FAILED:
      return false;
    default:
      return state;
  }
}

export function fetchingAdList(state = true, action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_ADS_REQUESTED:
      return true;
    case adReviewActionTypes.FETCH_ADS_SUCCESS:
      return false;
    case adReviewActionTypes.FETCH_ADS_FAILED:
      return false;
    default:
      return state;
  }
}

export function refreshingAdList(state = true, action) {
  switch (action.type) {
    case filterAdActionTypes.UPDATE_AD_FILTERS:
    case adReviewActionTypes.REFRESH_ADS_REQUESTED:
      return true;
    case adReviewActionTypes.REFRESH_ADS_SUCCESS:
      return false;
    case adReviewActionTypes.REFRESH_ADS_FAILED:
      return false;
    default:
      return state;
  }
}

export function fetchingReviewAudioList(state = true, action) {
  switch (action.type) {
    case adReviewActionTypes.REVIEW_AUDIO_ADS_REQUESTED:
      return true;
    case adReviewActionTypes.REVIEW_AUDIO_ADS_SUCCESS:
    case adReviewActionTypes.REVIEW_AUDIO_ADS_FAILED:
      return false;
    default:
      return state;
  }
}

export function reviewAudioById(state = {}, action) {
  switch (action.type) {
    case adReviewActionTypes.REVIEW_AUDIO_ADS_SUCCESS:
      return {
        ...action.ads.reduce((obj, ad) => {
          obj[ad.id] = ad;
          return obj;
        }, {}),
      };
    default:
      return state;
  }
}

export function byId(state = {}, action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_AD_SUCCESS:
      return {
        ...state,
        [action.ad.id]: action.ad,
      };
    case adReviewActionTypes.FETCH_ADS_SUCCESS:
      return {
        ...state,
        ...action.ads.reduce((obj, ad) => {
          obj[ad.id] = ad;
          return obj;
        }, {}),
      };
    case adReviewActionTypes.REFRESH_ADS_SUCCESS:
      return {
        ...action.ads.reduce((obj, ad) => {
          obj[ad.id] = ad;
          return obj;
        }, {}),
      };
    case debug.EXTEND_START_DATE:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          dateBegin: action.payload.newStartDate,
        },
      };
    case debug.EXTEND_END_DATE:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          dateEnd: action.payload.newEndDate,
        },
      };
    default:
      return state;
  }
}

export function visibleIds(state = [], action) {
  switch (action.type) {
    case adReviewActionTypes.FETCH_ADS_SUCCESS:
      return state.concat(action.ads.map(ad => ad.id));
    default:
      return state;
  }
}

export function refreshIds(state = [], action) {
  switch (action.type) {
    case adReviewActionTypes.REFRESH_ADS_SUCCESS:
      return action.ads.map(ad => ad.id);
    default:
      return state;
  }
}

export function adFilters(state = DEFAULT_PARAMS, action) {
  switch (action.type) {
    case filterAdActionTypes.UPDATE_AD_FILTERS:
      return {
        ...state,
        ...action.payload,
      };
    case filterAdActionTypes.UPDATE_SEARCH_WORD:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
}

export function reviewAudioIds(state = [], action) {
  switch (action.type) {
    case adReviewActionTypes.REVIEW_AUDIO_ADS_SUCCESS:
      return action.ads.map(ad => ad.id);
    default:
      return state;
  }
}

const initialFilterState = {
  activeStatus: [],
  excludedStatus: [],
};

export function filters(state = initialFilterState, action) {
  switch (action.type) {
    case adReviewActionTypes.ACTIVE_STATUS_FILTER_UPDATED:
      return {
        ...state,
        activeStatus: action.status,
      };
    default:
      return state;
  }
}

export function userInputNameSearch(state = '', action) {
  switch (action.type) {
    case adReviewActionTypes.UPDATE_NAME_FILTER:
      return action.pattern;
    default:
      return state;
  }
}

const initialPagingState = {
  limit: PAGINATION_PAGE_LIMIT,
  offset: 0,
  total: 0,
};

export function paging(state = initialPagingState, action) {
  switch (action.type) {
    case adReviewActionTypes.UPDATE_PAGING_INFO:
      return {
        ...state,
        total: action.paging.total,
        offset: state.limit + state.offset,
      };
    default:
      return state;
  }
}

export function reviewAudioPaging(state = initialPagingState, action) {
  switch (action.type) {
    case adReviewActionTypes.UPDATE_REVIEW_AUDIO_PAGING_INFO:
      return {
        ...state,
        total: action.paging.total,
        offset: state.limit + state.offset,
      };
    default:
      return state;
  }
}

export function refreshAdsError(state = '', action) {
  switch (action.type) {
    case adReviewActionTypes.REFRESH_ADS_FAILED:
      return action.err;
    case adReviewActionTypes.REFRESH_ADS_SUCCESS:
      return '';
    default:
      return state;
  }
}

// Currently unused. May be useful when more metadata specific to single ad exists
export function activeAd(state = {}, action) {
  switch (action.type) {
    case adReviewActionTypes.RECEIVE_AD:
      return { ...action.ad };
    default:
      return state;
  }
}

export default combineReducers({
  approval,
  filters,
  byId,
  reviewAudioById,
  visibleIds,
  refreshIds,
  reviewAudioIds,
  userInputNameSearch,
  paging,
  reviewAudioPaging,
  fetchingAd,
  fetchingAdList,
  refreshingAdList,
  fetchingReviewAudioList,
  refreshAdsError,
  adNotFound,
  adFilters,
  onboarding,
});

// Review Audio Utils
function getReviewAudioAd(state, id) {
  return state.reviewAudioById[id];
}

function reviewAudioExcludedStatus(state, id) {
  const ad = getReviewAudioAd(state, id);
  return !state.filters.excludedStatus.some(s => s === ad.adState);
}

function reviewAudioByUserInputAdName(state, id) {
  const ad = getReviewAudioAd(state, id);
  return fuzzyMatch(ad.internalName, state.userInputNameSearch);
}

// Utils
function getAd(state, id) {
  return state.byId[id];
}

function excludedStatus(state, id) {
  const ad = getAd(state, id);
  return !state.filters.excludedStatus.some(s => s === ad.adState);
}

function byStatus(state, id) {
  if (state.filters.activeStatus.length === 0) return true;

  const ad = getAd(state, id);
  return state.filters.activeStatus.some(s => s === ad.adState);
}

function byUserInputAdName(state, id) {
  const ad = getAd(state, id);
  return fuzzyMatch(ad.internalName, state.userInputNameSearch);
}

export function getAds(state) {
  return state.visibleIds
    .filter(byStatus.bind(null, state))
    .filter(excludedStatus.bind(null, state))
    .filter(byUserInputAdName.bind(null, state))
    .map(id => getAd(state, id));
}

export function refreshAdsById(state) {
  return state.refreshIds
    .filter(byStatus.bind(null, state))
    .filter(excludedStatus.bind(null, state))
    .filter(byUserInputAdName.bind(null, state))
    .map(id => getAd(state, id));
}

export function reviewAudioAdsById(state) {
  return state.reviewAudioIds
    .filter(byStatus.bind(null, state))
    .filter(reviewAudioExcludedStatus.bind(null, state))
    .filter(reviewAudioByUserInputAdName.bind(null, state))
    .map(id => getReviewAudioAd(state, id));
}



// WEBPACK FOOTER //
// ./src/reducers/ads.js